import { GiftDeliveryZonePolicy } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { GiftDeliveryFeePreview } from '@shared/api/giftOrder/giftOrder.interface';

/**
 * "몇 개만 더 담으면 기본배송비 무료" 안내 계산 — 권역 배송비 브랜드 전용.
 * 서버 소계는 권역 브랜드 라인의 (판매가 + 옵션가) × 수량 합이라(배송비·소량추가금 제외) 프론트에서 같은 식으로 역산한다.
 * 확정 금액은 언제나 미리보기 API가 다시 돌려주므로 여기 값은 "안내"에만 쓴다.
 */

/** 무료 기준까지 필요한 추가 수량 — 주문단위(orderUnit) 배수로 올림. 단가가 0 이하면 계산 불가(null) */
export const unitsToReachAmount = (
  remaining: number,
  unitPrice: number,
  orderUnit = 1,
): number | null => {
  if (remaining <= 0) return 0;
  if (!(unitPrice > 0)) return null;
  const raw = Math.ceil(remaining / unitPrice);
  const step = orderUnit > 1 ? orderUnit : 1;
  return Math.ceil(raw / step) * step;
};

/** 추가 담기 후보 라인 (권역 브랜드 상품만 넘길 것) */
export interface FreeShippingNudgeLine {
  /** 호출부가 라인을 되찾는 키 (장바구니는 itemId+옵션 시그니처) */
  key: string;
  itemName: string;
  /** 판매가 + 옵션가 (소량추가금 제외 — 서버 소계 기준과 동일) */
  unitPrice: number;
  orderUnit: number;
}

export interface FreeShippingSuggestion {
  key: string;
  itemName: string;
  addQty: number;
  addCost: number;
}

/**
 * 어느 상품을 몇 개 더 담으면 되는지 — 추가 결제액이 가장 작은 라인을 고른다.
 * (같은 비용이면 앞 라인 = 먼저 담은 상품)
 */
export const pickFreeShippingSuggestion = (
  remaining: number,
  lines: FreeShippingNudgeLine[],
): FreeShippingSuggestion | null => {
  if (remaining <= 0) return null;
  let best: FreeShippingSuggestion | null = null;
  for (const line of lines) {
    const addQty = unitsToReachAmount(remaining, line.unitPrice, line.orderUnit);
    if (!addQty) continue;
    const addCost = addQty * line.unitPrice;
    if (!best || addCost < best.addCost) {
      best = { key: line.key, itemName: line.itemName, addQty, addCost };
    }
  }
  return best;
};

/** 체크아웃 라인 — 브랜드가 미리보기의 권역 브랜드와 같은 라인만 후보가 된다 */
export interface FreeShippingCheckoutLine extends FreeShippingNudgeLine {
  brandId?: string;
}

/**
 * 미리보기 응답 + 주문 라인 → "어느 상품 몇 개 더" 제안.
 * 기본배송비가 붙어 있고(>0) 무료 기준에 못 미칠 때만 값이 나온다. 소계는 서버가 준 값을 그대로 쓴다.
 */
export const resolveFreeShippingSuggestion = (
  preview: GiftDeliveryFeePreview | null | undefined,
  lines: FreeShippingCheckoutLine[],
): FreeShippingSuggestion | null => {
  if (!preview || !preview.applicable || !preview.deliverable) return null;
  if (!preview.rule || preview.baseFee <= 0) return null;
  const brandId = preview.brand._id;
  return pickFreeShippingSuggestion(
    preview.rule.freeOverAmount - preview.subtotal,
    lines.filter((line) => line.brandId === brandId),
  );
};

/**
 * 주소를 모를 때 쓰는 무료 기준 단계 — 기본 기준(label '')이 맨 앞, 그 뒤로 기준이 다른 예외 지역(금액 오름차순).
 * 기본 기준과 금액이 같은 지역 규칙은 기본에 흡수된다. ("그 외 지역" 같은 표현을 만들지 않으려는 구조)
 */
export interface ZoneFreeTier {
  freeOverAmount: number;
  /** 예외 지역 표기 — "서울". 기본 기준이면 빈 문자열 */
  label: string;
}

export const resolveZoneFreeTiers = (
  policy?: GiftDeliveryZonePolicy | null,
): ZoneFreeTier[] => {
  if (!policy?.enabled) return [];
  const base =
    policy.defaultBaseFee &&
    policy.defaultBaseFee.fee > 0 &&
    policy.defaultBaseFee.freeOverAmount > 0
      ? policy.defaultBaseFee.freeOverAmount
      : null;

  const byAmount = new Map<number, string[]>();
  (policy.baseFeeRules ?? []).forEach((rule) => {
    if (!(rule.fee > 0) || !(rule.freeOverAmount > 0)) return; // 원래 무료인 지역은 안내 대상 아님
    if (rule.freeOverAmount === base) return;
    byAmount.set(rule.freeOverAmount, [
      ...(byAmount.get(rule.freeOverAmount) ?? []),
      rule.sidoIn?.length ? rule.sidoIn.join('·') : '일부 지역',
    ]);
  });
  const exceptions = Array.from(byAmount.entries())
    .sort(([x], [y]) => x - y)
    .map(([freeOverAmount, labels]) => ({ freeOverAmount, label: labels.join('·') }));

  return base != null ? [{ freeOverAmount: base, label: '' }, ...exceptions] : exceptions;
};

/** 소량주문 추가금 후보 라인 — 추가금 설정은 호출부가 resolveShippingFeeConfig로 풀어서 넘긴다 */
export interface SurchargeNudgeLine {
  key: string;
  itemName: string;
  /** 판매가 + 옵션가 (추가금 제외) */
  unitPrice: number;
  quantity: number;
  orderUnit: number;
  /** 개당 추가금 */
  shippingFee?: number;
  /** 이 수량 이상이면 추가금 면제 */
  freeShippingMinQuantity?: number;
}

export interface SurchargeSuggestion {
  key: string;
  itemName: string;
  addQty: number;
  /** 개당 추가금 */
  unitSurcharge: number;
  /** 지금 붙어 있는 추가금 합 (개당 × 수량) — 더 담으면 사라지는 금액 */
  surchargeTotal: number;
  /** 더 담았을 때 결제액 변화 (추가 상품값 − 사라지는 추가금). 음수면 더 담는 쪽이 오히려 싸다 */
  netAddCost: number;
}

/**
 * "N개 더 담으면 소량주문 추가금이 사라져요" — 추가금이 붙은 라인 중 결제액 증가가 가장 작은 라인.
 * 수량은 orderUnit씩만 움직이므로 면제 기준 이상이 되는 첫 격자 수량까지 올린다.
 */
export const pickSurchargeSuggestion = (
  lines: SurchargeNudgeLine[],
): SurchargeSuggestion | null => {
  let best: SurchargeSuggestion | null = null;
  for (const line of lines) {
    const { shippingFee, freeShippingMinQuantity: minQty, quantity } = line;
    if (!shippingFee || shippingFee <= 0 || minQty == null || quantity >= minQty) continue;
    const step = line.orderUnit > 1 ? line.orderUnit : 1;
    const addQty = Math.ceil((minQty - quantity) / step) * step;
    const surchargeTotal = shippingFee * quantity;
    const netAddCost = addQty * line.unitPrice - surchargeTotal;
    if (!best || netAddCost < best.netAddCost) {
      best = {
        key: line.key,
        itemName: line.itemName,
        addQty,
        unitSurcharge: shippingFee,
        surchargeTotal,
        netAddCost,
      };
    }
  }
  return best;
};
